/**
 * Checkbox — 14 px sharp box with optional inline label.
 * Native input is visually hidden; the visual indicator is a `<span>` next
 * to it driven by `peer-checked`. Sharp corners are the design's signature.
 *
 * Not present in the form artboards — extrapolated within the language:
 * 1 px line border, ink fill + paper check on checked, accent on focus.
 */

import type { InputHTMLAttributes, ReactNode, Ref } from 'react';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  ref?: Ref<HTMLInputElement>;
}

export function Checkbox({
  label,
  className = '',
  disabled,
  ref,
  ...rest
}: CheckboxProps) {
  const wrapper = label
    ? `inline-flex items-center gap-2.5 ${disabled ? 'opacity-60' : 'cursor-pointer'}`
    : 'inline-flex';

  return (
    <label className={`${wrapper} ${className}`}>
      <input
        {...rest}
        ref={ref}
        type="checkbox"
        disabled={disabled}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className="
          grid size-[14px] flex-shrink-0 place-items-center border border-line bg-paper
          text-[10px] leading-none text-paper transition-colors
          peer-hover:border-ink
          peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-accent peer-focus-visible:outline-offset-2
          peer-checked:border-ink peer-checked:bg-ink
          peer-checked:[&>span]:opacity-100
        "
      >
        <span className="opacity-0 transition-opacity">✓</span>
      </span>
      {label && <span className="text-[13px] leading-tight text-ink">{label}</span>}
    </label>
  );
}
